import React from "react";
import {
  AppBar,
  Container,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  Button,
  Card,
  CardActions,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { KeyboardDatePicker } from "@material-ui/pickers";
import NumberFormat from "react-number-format";
import RangeDictionary from "./RangeDictionary";

const flatTaxRate = (rate) =>
  RangeDictionary({
    [[0, Infinity]]: rate,
  });

const zeroTaxRate = flatTaxRate(0);

const states = {
  AL: {
    name: "Alabama",
    taxRange: flatTaxRate(0.05),
  },
  AK: { name: "Alaska", taxRange: zeroTaxRate },
  AZ: { name: "Arizona", taxRange: zeroTaxRate },
  AR: { name: "Arkansas", taxRange: flatTaxRate(0.069) },
  CA: { name: "California", taxRange: flatTaxRate(0.066) },
  CO: { name: "Colorado", taxRange: flatTaxRate(0.0463) },
  CT: { name: "Connecticut", taxRange: zeroTaxRate },
  DE: { name: "Delaware", taxRange: zeroTaxRate },
  DC: { name: "District Of Columbia", taxRange: zeroTaxRate },
  FL: { name: "Florida", taxRange: zeroTaxRate },
  GA: {
    name: "Georgia",
    taxRange: RangeDictionary({
      [[0, 8000]]: 0.02,
      [[8000, 10000]]: 0.03,
      [[10000, 12000]]: 0.04,
      [[12000, 15000]]: 0.05,
      [[15000, Infinity]]: 0.0575,
    }),
  },
  HI: { name: "Hawaii", taxRange: zeroTaxRate },
  ID: { name: "Idaho", taxRange: flatTaxRate(0.06925) },
  IL: { name: "Illinois", taxRange: flatTaxRate(0.0495) },
  IN: { name: "Indiana", taxRange: flatTaxRate(0.0323) },
  IA: { name: "Iowa", taxRange: flatTaxRate(0.06) },
  KS: { name: "Kansas", taxRange: flatTaxRate(0.05) },
  KY: { name: "Kentucky", taxRange: flatTaxRate(0.05) },
  LA: { name: "Louisiana", taxRange: zeroTaxRate },
  ME: { name: "Maine", taxRange: flatTaxRate(0.05) },
  MD: { name: "Maryland", taxRange: flatTaxRate(0.0575) },
  MA: { name: "Massachusetts", taxRange: flatTaxRate(0.05) },
  MI: { name: "Michigan", taxRange: flatTaxRate(0.0425) },
  MN: { name: "Minnesota", taxRange: flatTaxRate(0.0625) },
  MS: { name: "Mississippi", taxRange: zeroTaxRate },
  MO: { name: "Missouri", taxRange: flatTaxRate(0.054) },
  MT: { name: "Montana", taxRange: flatTaxRate(0.06) },
  NE: { name: "Nebraska", taxRange: flatTaxRate(0.05) },
  NV: { name: "Nevada", taxRange: zeroTaxRate },
  NH: { name: "New Hampshire", taxRange: zeroTaxRate },
  NJ: { name: "New Jersey", taxRange: zeroTaxRate },
  NM: { name: "New Mexico", taxRange: flatTaxRate(0.049) },
  NY: { name: "New York", taxRange: flatTaxRate(0.0962) },
  NC: { name: "North Carolina", taxRange: flatTaxRate(0.0535) },
  ND: { name: "North Dakota", taxRange: flatTaxRate(0.0184) },
  OH: { name: "Ohio", taxRange: flatTaxRate(0.035) },
  OK: { name: "Oklahoma", taxRange: flatTaxRate(0.05) },
  OR: { name: "Oregon", taxRange: flatTaxRate(0.08) },
  PA: { name: "Pennsylvania", taxRange: flatTaxRate(0.0307) },
  RI: { name: "Rhode Island", taxRange: flatTaxRate(0.0599) },
  SC: { name: "South Carolina", taxRange: flatTaxRate(0.07) },
  SD: { name: "South Dakota", taxRange: zeroTaxRate },
  TN: { name: "Tennessee", taxRange: zeroTaxRate },
  TX: { name: "Texas", taxRange: zeroTaxRate },
  UT: { name: "Utah", taxRange: zeroTaxRate },
  VT: { name: "Vermont", taxRange: flatTaxRate(0.0528) },
  VA: { name: "Virginia", taxRange: flatTaxRate(0.0575) },
  WA: { name: "Washington", taxRange: zeroTaxRate },
  WV: {
    name: "West Virginia",
    taxRange: RangeDictionary({
      [[0, 10000]]: 0.03,
      [[10000, 25000]]: 0.04,
      [[25000, 40000]]: 0.045,
      [[40000, 60000]]: 0.06,
      [[60000, Infinity]]: 0.065,
    }),
  },
  WI: {
    name: "Wisconsin",
    taxRange: RangeDictionary({
      [[0, 10910]]: 0.04,
      [[10910, 21820]]: 0.0584,
      [[21820, 240190]]: 0.0627,
      [[240190, Infinity]]: 0.0765,
    }),
  },
  WY: { name: "Wyoming", taxRange: zeroTaxRate },
};

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const useStyles = makeStyles((theme) => {
  const background = "#9FBDA7";

  return {
    actions: {
      justifyContent: "flex-end",
    },
    appBar: {
      backgroundColor: background,
      boxShadow: "none",
      paddingBottom: theme.spacing(4),
    },
    callToAction: {
      fontSize: theme.typography.h5.fontSize,
    },
    navLink: {
      color: theme.palette.primary.contrastText,
    },
    faqTitle: {
      marginBottom: theme.spacing(1),
    },
    formRoot: {
      display: "flex",
      flexWrap: "wrap",
      "& .MuiTextField-root": {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
      },
    },
    formControl: {
      flexGrow: 1,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(4),
    },
    mainText: {
      fontWeight: 400,
      color: theme.palette.secondary.contrastText,
      textShadow:
        "0px 2px 1px rgba(0,0,0,0.2), 0px 1px 1px rgba(0,0,0,0.14), 0px 1px 3px rgba(0,0,0,0.12)",
    },
    subText: {
      opacity: 0.75,
      color: theme.palette.secondary.contrastText,
    },
    root: {
      flexGrow: 1,
      backgroundColor: background,
      overflowY: "scroll",
      overflowX: "hidden",
    },
    paper: {
      color: "#5F6075",
      borderRadius: 8,
      flexGrow: 1,
      padding: theme.spacing(2),
      backgroundColor: theme.palette.info.light,
    },
    title: {
      flexGrow: 1,
    },
    textField: {
      fontSize: theme.typography.h5.fontSize,
    },
    input: {
      marginTop: theme.spacing(1),
      fontSize: theme.typography.h5.fontSize,
    },
    label: {
      fontSize: theme.typography.h5.fontSize,
    },
    formHelperText: {
      fontSize: 14,
    },
    breakdownTable: {
      marginTop: theme.spacing(2),
    },
    breakdownTableRow: {
      root: {
        "&:nth-of-type(odd)": {
          backgroundColor: "rgba(0,0,0,0.5)",
        },
      },
    },
  };
});

const stateTax = (salary, rsuWorth, state) => {
  const taxRate = states[state].taxRange[salary];
  return rsuWorth * taxRate;
};

const socialSecurityTax = (salary, additionalEarnings, rsuWorth, date) => {
  const rate = 0.062;
  const ceiling = 8537.4;
  const month = date.getMonth();
  const dayOfMonth = date.getDate();

  // `getMonth` is a 0 based index
  // So we can safely, roughly double the returned index by 2 (because jan is 0, it'll result in 0 pay periods)
  let payPeriods = month * 2;
  // if the month is not January (0) & it's past the 15th, we can safely add two more pay periods for the current month (remember the 0-based index)
  if (month > 0 && dayOfMonth >= 15) {
    payPeriods += 2;
    // if it is january and we are past the halfway point, we can safely add one pay period
  } else if (month === 0 && dayOfMonth >= 15) {
    payPeriods += 1;
  }

  // Let's just assume two paychecks per month
  const payPeriodPay = salary / 24;
  // And based on the amount of pay periods that have transpired, what has been earned
  const earnedIncome = (payPeriodPay * payPeriods) + additionalEarnings;
  // Naive calculation of much social security you've paid
  const socialSecurityPaid = earnedIncome * rate;
  // But, there's a max social security https://www.ssa.gov/OACT/COLA/cbb.html, so take what's lower
  const paidToDate = Math.min(ceiling, socialSecurityPaid);
  // Based on what's been paid, see what's remaining. Should always be >= 0.
  const remaining = ceiling - paidToDate;
  const stockWorthTaxTotal = rsuWorth * rate;

  return Math.min(remaining, stockWorthTaxTotal);
};

const medicareTax = (salary) => salary * 0.0145;

const federalTax = (salary) => salary * 0.22;

const calculateTaxes = (salary, additionalEarnings, stockWorth, state, date) => {
  const totalEarnings = salary + additionalEarnings;
  const statePaid = stateTax(totalEarnings, stockWorth, state);
  const socialSecurity = socialSecurityTax(salary, additionalEarnings, stockWorth, date);
  const medicare = medicareTax(stockWorth);
  const federal = federalTax(stockWorth);

  return {
    state: { name: "State Tax", value: statePaid },
    socialSecurity: { name: "Social Security Tax", value: socialSecurity },
    medicare: { name: "Medicare Tax", value: medicare },
    federal: { name: "Federal Income Tax", value: federal },
    total: {
      name: "Total Taxes",
      value: statePaid + socialSecurity + medicare + federal,
    },
  };
};

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
    />
  );
}

function App() {
  const classes = useStyles();
  const [residingState, setResidingState] = React.useState();
  const [salary, setSalary] = React.useState();
  const [additionalEarnings, setAdditionalEarnings] = React.useState();
  const [pricePerShare, setPricePerShare] = React.useState();
  const [rsuQuantity, setRsuQuantity] = React.useState();
  const [dateOfVesting, setDateOfVesting] = React.useState(null);
  const firstFieldRef = React.useRef();
  const formRef = React.useRef();
  const faqRef = React.useRef();

  const handleResidingStateChange = (event) => {
    setResidingState(event.target.value);
  };
  const handleSalaryChange = (event) => {
    setSalary(event.target.value);
  };
  const handleAdditionalEarningsChange = (event) => {
    setAdditionalEarnings(event.target.value);
  };
  const handlePricePerShareChange = (event) => {
    setPricePerShare(event.target.value);
  };
  const handleRsuQuantityChange = (event) => {
    setRsuQuantity(event.target.value);
  };
  const handleDateOfVestingChange = (date) => {
    setDateOfVesting(date);
  };
  const handleResetClick = () => {
    setResidingState("");
    setSalary("");
    setPricePerShare("");
    setRsuQuantity("");
    setDateOfVesting(null);
  };
  const handleGetStartedClick = () => {
    firstFieldRef.current.focus();
    formRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };
  const handleFaqClick = () => {
    faqRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  let payouts = {
    gross: 0,
    net: 0,
    breakdown: {},
  };
  if (
    salary &&
    pricePerShare &&
    rsuQuantity &&
    dateOfVesting &&
    residingState
  ) {
    let defaultedAdditionalEarnings = additionalEarnings || 0;
    payouts.gross = parseFloat(pricePerShare * rsuQuantity).toFixed(2);
    const { total, ...breakdown } = calculateTaxes(
      salary,
      defaultedAdditionalEarnings,
      payouts.gross,
      residingState,
      dateOfVesting
    );
    payouts.net = parseFloat(pricePerShare * rsuQuantity - total.value).toFixed(
      2
    );
    payouts.breakdown = { ...breakdown };
    payouts.breakdownTotal = total;
  }

  return (
    <div className={classes.root}>
      <AppBar position="static" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Free RSU Calculator
          </Typography>
          <Button className={classes.navLink} href="#">
            Home
          </Button>
          <Button className={classes.navLink} onClick={handleFaqClick}>
            FAQ
          </Button>
        </Toolbar>
      </AppBar>
      <Container fixed>
        <Grid container spacing={8}>
          <Grid
            direction="column"
            container
            item
            spacing={4}
            alignItems="center"
          >
            <Grid item>
              <Typography
                className={classes.mainText}
                variant="h2"
                component="h1"
                align="center"
              >
                Know your stock's worth
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                className={classes.subText}
                variant="h5"
                component="h2"
                align="center"
              >
                Don't be surprised on your big payday.
                <br />
                Estimate how much you'll be seeing in the bank when your stocks
                vest, for free.
              </Typography>
            </Grid>
            <Grid item>
              <Button
                className={classes.callToAction}
                variant="contained"
                size="large"
                color="secondary"
                onClick={handleGetStartedClick}
              >
                Get Started
              </Button>
            </Grid>
          </Grid>
          <Grid xs={12} item ref={formRef}>
            <Card className={classes.paper}>
              <CardContent>
                <form className={classes.formRoot}>
                  <TextField
                    className={classes.textField}
                    type="number"
                    label="Number of RSUs"
                    helperText="How many stocks you are granted"
                    value={rsuQuantity}
                    onChange={handleRsuQuantityChange}
                    fullWidth
                    inputRef={firstFieldRef}
                    FormHelperTextProps={{
                      classes: { root: classes.formHelperText },
                    }}
                    InputProps={{
                      classes: { input: classes.input },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: { root: classes.label },
                    }}
                  />
                  <TextField
                    className={classes.textField}
                    label="Price Per Share"
                    helperText="The current price per share of stock"
                    value={pricePerShare}
                    onChange={handlePricePerShareChange}
                    fullWidth
                    FormHelperTextProps={{
                      classes: { root: classes.formHelperText },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: { root: classes.label },
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      classes: { input: classes.input },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="MM/dd/yyyy"
                    fullWidth
                    margin="normal"
                    label="Vesting Date"
                    value={dateOfVesting}
                    onChange={handleDateOfVestingChange}
                    helperText="We use this to figure out how much social security you will have paid already by the time your stocks vest"
                    FormHelperTextProps={{
                      classes: { root: classes.formHelperText },
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: { root: classes.label },
                    }}
                    InputProps={{
                      classes: { input: classes.input },
                    }}
                  />
                  <TextField
                    className={classes.textField}
                    label="Salary"
                    helperText="We use this to figure out your federal income, medicare and social security taxes"
                    fullWidth
                    value={salary}
                    onChange={handleSalaryChange}
                    FormHelperTextProps={{
                      classes: { root: classes.formHelperText },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: { root: classes.label },
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      classes: { input: classes.input },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    className={classes.textField}
                    label="Additional earnings"
                    helperText="We use this, in combination with your salary, to figure out how much social security you will have paid already by the time your stocks vest"
                    fullWidth
                    value={additionalEarnings}
                    onChange={handleAdditionalEarningsChange}
                    FormHelperTextProps={{
                      classes: { root: classes.formHelperText },
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: { root: classes.label },
                    }}
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                      classes: { input: classes.input },
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />
                  <FormControl className={classes.formControl}>
                    <InputLabel
                      shrink
                      id="demo-simple-select-placeholder-label-label"
                      className={classes.label}
                    >
                      State of Residence
                    </InputLabel>
                    <Select
                      classes={{ root: classes.input }}
                      labelId="demo-simple-select-placeholder-label-label"
                      fullWidth
                      value={residingState}
                      onChange={handleResidingStateChange}
                      displayEmpty
                    >
                      {Object.keys(states).map((stateAbbreviation) => (
                        <MenuItem
                          key={stateAbbreviation}
                          value={stateAbbreviation}
                        >
                          {states[stateAbbreviation].name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText className={classes.formHelperText}>
                      We use this to figure out any applicable state taxes
                    </FormHelperText>
                  </FormControl>
                </form>
              </CardContent>
              <CardActions className={classes.actions}>
                <Button size="small" color="primary" onClick={handleResetClick}>
                  Reset
                </Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid container item spacing={4}>
            <Grid item xs={12} sm={6}>
              <Card className={classes.paper}>
                <CardContent>
                  <Typography variant="h5" componet="h2">
                    Before taxes
                  </Typography>
                  <Typography variant="h3">
                    {moneyFormatter.format(payouts.gross)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card className={classes.paper}>
                <CardContent>
                  <Typography variant="h5" component="h2">
                    After taxes
                  </Typography>
                  <Typography variant="h3">
                    {moneyFormatter.format(payouts.net)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card className={classes.paper}>
                <CardContent>
                  <Typography variant="h5" component="h2">
                    Tax Breakdown
                  </Typography>
                  <TableContainer className={classes.breakdownTable}>
                    <Table>
                      <TableBody>
                        {Object.values(payouts.breakdown).length ? (
                          <>
                            {Object.values(payouts.breakdown).map(
                              ({ name, value }) => (
                                <TableRow classes={classes.breakdownTableRow}>
                                  <TableCell>{name}</TableCell>
                                  <TableCell>
                                    {moneyFormatter.format(value)}
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                            <TableRow classes={classes.breakdownTableRow}>
                              <TableCell>
                                <b>{payouts.breakdownTotal.name}</b>
                              </TableCell>
                              <TableCell>
                                <b>
                                  {moneyFormatter.format(
                                    payouts.breakdownTotal.value
                                  )}
                                </b>
                              </TableCell>
                            </TableRow>
                          </>
                        ) : (
                          <Typography component="h3">
                            Not enough information yet. Fill out the calculator
                            form above.
                          </Typography>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Grid item xs={12} ref={faqRef}>
            <Card className={classes.paper}>
              <CardContent>
                <Typography
                  className={classes.faqTitle}
                  variant="h5"
                  component="h2"
                >
                  Restricted Stock Units
                </Typography>
                <Typography component="h3">
                  Restricted Stock Units, also called RSUs, are stocks that your
                  company grants you and that you hold, but aren't able to sell
                  until a milestone is acheived. Typically the milestone is on a
                  vesting schedule and is associated with a length of employment
                  or a performance goal.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card className={classes.paper}>
              <CardContent>
                <Typography
                  className={classes.faqTitle}
                  variant="h5"
                  component="h2"
                >
                  Vesting Schedules
                </Typography>
                <Typography component="h3">
                  Most of the time a vesting schedule is literally that, a
                  schedule. You have to work at the company until a certain date
                  until you can sell those stocks. An example of a RSU Vesting
                  Schedule is you are granted 1,200 stocks and, after 1 year of
                  employment, 600 of those stocks are sellable. Then, every 3
                  months, 200 more of those stocks become yours to sell. Often
                  times, if you quit or are terminated before stocks vest, even
                  if it's the day before, you forfeit those stocks and they are
                  no longer yours to sell. Any that have vested before you left
                  the company that you haven't sold are still yours.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card className={classes.paper}>
              <CardContent>
                <Typography
                  className={classes.faqTitle}
                  variant="h5"
                  component="h2"
                >
                  Taxation
                </Typography>
                <Typography component="h3">
                  You know the saying about death and taxes. RSUs are no
                  exception. Almost all of the time, taxes are taken out at the
                  time your restricted stock units vest. Like a regular
                  paycheck, your total payout (rsu quantity vested * price per
                  share) is taxed by:
                  <ul>
                    <li>Federal supplemental wage rate (22%)</li>
                    <li>Medicare (1.45%)</li>
                    <li>
                      Social security (6.2% social security has a total tax
                      ceiling for all wages earned of $8,537.40)
                    </li>
                    <li>Any state or local taxes you regularly pay</li>
                  </ul>
                  We do our best to estimate all of this for you, taking into
                  account your salary & residence and display it in the tax
                  breakdown above. Typically, these taxes are taken out in the
                  form of stocks. So, if your total tax rate is 25% and 100
                  shares vested, you'd see 75 shares and your company sets aside
                  25 shares to pay taxes on your behalf.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
}

export default App;
